import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LightboxContext = createContext({ open: () => {} });

export const useLightbox = () => useContext(LightboxContext);

export const LightboxProvider = ({ children }) => {
  const [src, setSrc] = useState(null);
  const [alt, setAlt] = useState("");

  const open = useCallback((imageSrc, imageAlt = "") => {
    setSrc(imageSrc);
    setAlt(imageAlt);
  }, []);

  const close = useCallback(() => {
    setSrc(null);
    setAlt("");
  }, []);

  useEffect(() => {
    if (!src) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [src, close]);

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {src && (
          <motion.div
            data-testid="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center p-6 md:p-12 cursor-zoom-out"
          >
            <button
              data-testid="lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                close();
              }}
              className="absolute top-6 right-6 md:top-10 md:right-10 text-bone hover:text-clay text-xs uppercase tracking-[0.22em] border border-bone/40 hover:border-clay px-5 py-3 transition-colors"
              aria-label="Close"
            >
              Close
            </button>
            <motion.img
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain cursor-default"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </LightboxContext.Provider>
  );
};
